import React, { useState } from 'react';
import { Mail, Send, Heart, Users, Handshake, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { subscribeToNewsletter } from '../lib/newsletter';

const GetInvolved = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const data = await subscribeToNewsletter(newsletterEmail);
      if (data.success) {
        toast.success(data.message || 'Thank you for subscribing!');
        setNewsletterEmail('');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error(error.message || 'An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1527525443983-6e60c75fff46)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 to-emerald-800/70"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Get Involved
          </h1>
          <p className="text-xl text-emerald-50 max-w-2xl mx-auto">
            Join us in creating sustainable change and empowering communities
          </p>
        </div>
      </section>

      {/* Volunteer Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div 
                  className="h-64 lg:h-auto bg-cover bg-center"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800)',
                  }}
                />
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                    <Users className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Volunteer</h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    We invite individuals to volunteer in our community programs, youth mentorship initiatives, 
                    skills training workshops, and local outreach activities. Your time and talents can make 
                    a real difference in the lives of those we serve.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Community program support
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Youth mentorship
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Skills training workshops
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Local outreach initiatives
                    </li>
                  </ul>
                  <Link to="/contact">
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="volunteer-contact-btn"
                    >
                      Get in Touch
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner With Us Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Card className="border-2 border-gray-200 shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
                  <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                    <Handshake className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Partner With Us</h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    We welcome partnerships with NGOs, community organizations, and corporate partners 
                    to collaborate on community development, youth empowerment, and sustainable livelihood 
                    projects. Together, we can amplify our impact and reach more communities in need.
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Community development projects
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Youth empowerment programs
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Sustainable livelihood initiatives
                    </li>
                    <li className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                      Corporate social responsibility
                    </li>
                  </ul>
                  <Link to="/contact">
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="partner-contact-btn"
                    >
                      Discuss Partnership
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
                <div 
                  className="h-64 lg:h-auto bg-cover bg-center order-1 lg:order-2"
                  style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800)',
                  }}
                />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Donate / Support Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Donate / Support Our Work</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Your generosity helps us continue our mission of empowering communities and creating 
              sustainable change. Every contribution, no matter the size, makes a meaningful difference.
            </p>
            
            <Card className="border-2 border-emerald-200 bg-emerald-50/50 shadow-md">
              <CardContent className="p-8">
                <p className="text-gray-700 mb-6">
                  Support our programs and help us reach more communities in need. Your donation 
                  directly funds youth empowerment, skills training, and sustainable development initiatives.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="https://buy.stripe.com/6oU4gsg9r9Nz3ce2GU0kE00" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => {
                      if (typeof window.gtag === 'function') {
                        window.gtag('event', 'donate_click', {
                          event_category: 'fundraising',
                          event_label: 'stripe_payment_link'
                        });
                      }
                    }}
                  >
                    <Button 
                      size="lg"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto px-8"
                      data-testid="donate-stripe-btn"
                    >
                      <Heart className="mr-2 w-5 h-5" />
                      Donate Now
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto px-8"
                      data-testid="donate-contact-btn"
                    >
                      <Mail className="mr-2 w-5 h-5" />
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Newsletter / Stay Updated Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6">
              <Mail className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Stay Updated</h2>
            <p className="text-xl text-emerald-50 mb-8">
              Sign up to receive updates on our programs, impact stories, volunteer opportunities, 
              and learn ways to support our work.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                required
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 h-12 bg-white border-white text-gray-900 placeholder:text-gray-500"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 h-12"
                data-testid="newsletter-subscribe-btn"
              >
                {isSubmitting ? 'Signing up...' : 'Subscribe'}
              </Button>
            </form>
            
            <p className="text-sm text-emerald-100 mt-4">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
